import { ReactNode } from "react";
import * as ChartJs from "chart.js";
import { Moment } from "moment";
import { ComponentBaseProps } from "./basePropTypes";
import { ChartEnums } from "models/enums";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { IconDefinition } from "./iconPropTypes";
import { CustomNodeElementProps, RawNodeDatum, TreeNodeDatum, TreeProps } from "react-d3-tree";
import { TimelineStepType } from "models/enums/chart";

export interface ChartProps extends ComponentBaseProps {
  id: string;
  datasets?: ChartContainerDataSet[];
  dataUnit?: string;
  legendPortalId?: string;
  useClickableChart?: boolean; // default false; when false, charts ignore clicks/freeze
}

export interface DoughnutChartProps extends ChartProps {
  title?: ReactNode;
  legendType: ChartEnums.LegendType;
  colorPaletteType: ChartEnums.DoughnutChartColorPalette;
  middleText?: string;
  remainingSumText?: string;
  onClick?: (index: number, label?: string | string[] | number | number[] | Date | Date[] | Moment | Moment[]) => void;
  onBackgroundClick?: () => void;
  hideLegend?: boolean;
  showTotal?: boolean;
}

export interface TwoDChartProps extends ChartProps {
  labels: Array<string | string[] | number | number[] | Date | Date[] | Moment | Moment[]>;
  tooltips?: Array<string | string[]>;
  maintainAspectRatio?: boolean;
  yAxisTickUnit?: ChartEnums.YAxisTickUnitType;
  overrideLabelFontSize?: number;
  classes?: {
    chartContainer?: string;
    canvas?: string;
    legend?: string;
  };
  onClick?: (
    index: number,
    datasetIndex: number,
    xValue?: string | number | Date | Moment,
    label?: string | string[] | number | number[] | Date | Date[] | Moment | Moment[]
  ) => void;
  onBackgroundClick?: () => void;
  hideLegend?: boolean;
  title?: ReactNode;
}

export interface NumberChartProps extends ChartProps {
  title: React.ReactNode;
  data: number;
  icon: IconDefinition;
}

export interface NumberChartTagProps {
  percentage: number;
}

export interface ChartDataSet {
  label?: string | string[];
  data?: number | Array<number | null | undefined | number[]> | ChartJs.ChartPoint[];
}

export interface ChartContainerDataSet extends ChartDataSet {
  isNotSelected?: boolean;
}

export interface BarChartColorPalette {
  backgroundColor?: string;
  backgroundImageSource?: string;
  borderColor: string;
}

export interface ChartPeriodClickArgs {
  start: Date;
  end: Date;
  index: number;
  datasetIndex: number;
  label?: string | number | string[] | number[] | Date | Date[] | Moment | Moment[];
}

export interface ChartViewProps {
  id?: string;
  chartAPIKey: string;
  chartCacheKey?: string;
  chartTitle?: ReactNode | ((data: WintSuperkollAPIModel.ReportWidget) => ReactNode);
  filter?: any;
  useSkeleton?: WintSuperkollAPIModel.ReportWidgetType;
  /* This is for all graphs except acyclic graphs (except for tree/forest). */
  apiToChartHandler?: (data?: WintSuperkollAPIModel.ReportWidget) => ChartDataSet[];
  /* This is only for acyclic graphs (tree/forest). */
  iconHandler?: (branchNode: WintSuperkollAPIModel.BranchNode) => IconDefinition | undefined;
  labelFormatter?: (label?: string) => string;
  /* This is only for bar charts for now. */
  toolTipFormatter?: (label?: string) => string;
  customLabelsHandler?: (data?: WintSuperkollAPIModel.ReportWidget) => string[];
  legendFormatter?: (index: number, name?: string, startDate?: Date, endDate?: Date) => string;
  onChartSuccessHandler?: (data?: WintSuperkollAPIModel.ReportWidget, filter?: any) => void;
  hideLegend?: boolean;
  dataUnit?: string;
  showTotal?: boolean;
  doughnutChartLegendType?: ChartEnums.LegendType;
  doughnutChartColorPalette?: ChartEnums.DoughnutChartColorPalette;
  showDoughnutChartMiddleText?: boolean;
  numberChartIcon?: IconDefinition;
  legendPortalId?: string;
  yAxisTickUnit?: ChartEnums.YAxisTickUnitType;
  overrideLabelFontSize?: number;
  useClickableChart?: boolean; // default false; forwarded to concrete chart components
  onPeriodClick?: (args: ChartPeriodClickArgs) => void;
  onDataPointClick?: (
    index: number,
    label?: string | string[] | number | number[] | Date | Date[] | Moment | Moment[]
  ) => void;
  onBackgroundClick?: () => void;
}

export interface ChartContainerProps extends ChartViewProps {
  accumulatedChartAPIKey?: string;
  usePageFilter?: boolean;
  filterToAPIHandler?: (filter: any) => any;
  onChartSuccessHandler?: (data?: WintSuperkollAPIModel.ReportWidget) => void;
  onDataPointClickedSetFilter?: (
    label?: string | string[] | number | number[] | Date | Date[] | Moment | Moment[] | ChartPeriodClickArgs
  ) => Record<string, any>;
  onBackgroundClickedSetFilter?: Record<string, any>;
}

export interface ChartAmountTitle {
  title: string;
  value: string;
}

export interface MultiLineChartTitleProps {
  rows: ChartAmountTitle[];
}

export interface ChartContextState {
  type: ChartEnums.DisplayType;
  setChartType: (type: ChartEnums.DisplayType) => void;
  filter: ChartContextFilter;
  setChartFilter: (filter: ChartContextFilter) => void;
  selectedChartId?: string | null;
  setSelectedChartId?: (id: string | null) => void;
  // Selection store APIs (React-based, replaces window-backed stores)
  getSelection?: (chartId: string) => ChartSelection | null;
  setSelection?: (chartId: string, selection: ChartSelection | null) => void;
  clearAllSelections?: () => void;
}

export interface ChartContextFilter {
  frequency: ChartEnums.ChartDataFrequency;
  chartType?: number;
}

export interface ChartSelection {
  index: number;
  datasetIndex?: number;
}

export interface ChartTypeSelectorProps {
  chartTypes?: any[];
  localization: string;
}

export class ChartKeyTypes {
  normal: string;
  accumulated: string;

  constructor(normal: string, acc: string) {
    this.normal = normal;
    this.accumulated = acc;
  }
}
export interface DashboardMainChartWrapperProps {
  chartTypes?: ChartEnums.MainDashBoardChartTypes[];
}

export interface CustomRawNodeDatum extends RawNodeDatum {
  icon?: IconDefinition;
  children?: CustomRawNodeDatum[];
}

export interface TreeDChartProps {
  id: string;
  data: CustomRawNodeDatum | CustomRawNodeDatum[];
}

export interface CustomeTreeNodeDatum extends CustomRawNodeDatum, TreeNodeDatum {
  children?: CustomeTreeNodeDatum[];
}

export interface CustomNode extends CustomNodeElementProps {
  nodeDatum: CustomeTreeNodeDatum;
}

export interface CustomTreeProps extends TreeProps {
  data: RawNodeDatum[] | RawNodeDatum;
}

export interface RectangularNodeProps {
  node: CustomNode;
}

//#region timeline
export interface TimelineItem {
  text: string;
  subText?: string | string[];
  type: TimelineStepType;
  toolTip?: string;
  subItems?: TimelineSubItem[];
}

export interface TimelineSubItem {
  text: string;
  subText?: string;
  type: TimelineStepType;
}

export interface SimpleTimelineProps {
  items: TimelineItem[];
}

export interface TimelineStepProps {
  item: TimelineItem;
  isLastStep: boolean;
  nextStepType?: TimelineStepType;
}

export interface HighlightedStepProps {
  icon?: React.ReactNode;
  title: string;
  description?: string | string[];
  toolTip?: string;
}
//#endregion
